import { ReplacedCounterEntry, UnitRecord } from './equipmentTypes';
import type { EquipmentCategory, SingleUnit } from './equipmentTypes';
import type { tGroupedEquipment } from '../../types';
import {
    GEN2_SINGLE_UNIT_FLOAT_FIELDS,
    GEN2_SINGLE_UNIT_WRITABLE_FIELDS,
} from './equipmentConstants';

/** Units from listSingleUnitsByCategoryId often omit nested `category`; resolve from company categories. */
export function attachCategoryToUnit(
    unit: SingleUnit,
    categories: EquipmentCategory[],
): SingleUnit {
    if (unit.category?.name) {
        return unit;
    }

    const category = categories.find((item) => item.id === unit.categoryId);
    if (!category) {
        return unit;
    }

    return {
        ...unit,
        category,
    };
}

type LocationRef = { id?: string | null };

type LocationPickerRef = {
    id: string;
    name: string;
    regionalDivisionId?: string | null;
    districtId?: string | null;
};
type RegionPickerRef = { id: string; name: string };
type DistrictPickerRef = { id: string; name: string };

/** All location ids known for the company (used to detect stale unit.locationId after location delete). */
export function collectCompanyLocationIds(...locationLists: LocationRef[][]): Set<string> {
    const ids = new Set<string>();
    for (const list of locationLists) {
        for (const loc of list) {
            if (loc?.id) {
                ids.add(loc.id);
            }
        }
    }
    return ids;
}

export function isUnitMissingOrUnknownLocation(
    unit: Pick<SingleUnit, 'locationId'>,
    companyLocationIds: Set<string>,
): boolean {
    if (!unit.locationId) {
        return true;
    }
    return !companyLocationIds.has(unit.locationId);
}

export function isValidCompanyLocationId(
    locationId: string | null | undefined,
    companyLocations: LocationPickerRef[],
): boolean {
    if (!locationId) {
        return false;
    }
    return companyLocations.some((location) => location.id === locationId);
}

/** Clears stale locationId so selects only use valid company locations. */
export function normalizeEquipmentUnitLocationId(
    locationId: string | null | undefined,
    companyLocations: LocationPickerRef[],
): string {
    if (!locationId) {
        return '';
    }
    return isValidCompanyLocationId(locationId, companyLocations) ? locationId : '';
}

export function formatCompanyLocationDisplayLabel(
    locationId: string | null | undefined,
    companyRegions: RegionPickerRef[],
    companyLocations: LocationPickerRef[],
    companyDistricts: DistrictPickerRef[],
): string {
    if (!locationId) {
        return 'Unassigned';
    }
    const loc = companyLocations.find((item) => item.id === locationId);
    if (!loc) {
        return 'Unassigned';
    }
    const district = companyDistricts.find((item) => item.id === loc.districtId);
    const region = companyRegions.find((item) => item.id === loc.regionalDivisionId);
    const parts = [region?.name, district?.name, loc.name].filter(Boolean);
    return parts.join(' › ');
}

export function buildCompanyLocationSelectOptions(
    companyRegions: RegionPickerRef[],
    companyLocations: LocationPickerRef[],
    companyDistricts: DistrictPickerRef[],
): Array<{ label: string; value: string }> {
    return [
        { label: '— Unassigned —', value: '' },
        ...companyRegions.flatMap((region) => {
            const regionLocations = companyLocations
                .filter((loc) => loc.regionalDivisionId === region.id)
                .sort((a, b) => a.name.localeCompare(b.name));
            return regionLocations.map((loc) => {
                const district = companyDistricts.find((item) => item.id === loc.districtId);
                const label = [region.name, district?.name, loc.name].filter(Boolean).join(' › ');
                return { label, value: loc.id };
            });
        }),
    ];
}

/** Company users: show units with no location, assigned locations, or orphaned locationId. */
export function isUnitVisibleOnEquipmentListForUser(
    unit: SingleUnit,
    userAssignedLocations: LocationRef[],
    companyLocationIds: Set<string>,
): boolean {
    if (!unit?.locationId) {
        return true;
    }
    if (userAssignedLocations.some((loc) => loc.id === unit.locationId)) {
        return true;
    }
    return !companyLocationIds.has(unit.locationId);
}

/** Merge partial/API layers without overwriting with null (preserves categoryId on partial GraphQL responses). */
export function mergeSingleUnitFields(
    existing: SingleUnit,
    ...layers: Array<Partial<SingleUnit> | null | undefined>
): SingleUnit {
    const merged: SingleUnit = { ...existing };
    for (const layer of layers) {
        if (!layer) {
            continue;
        }
        for (const [key, value] of Object.entries(layer)) {
            if (value !== null && value !== undefined) {
                (merged as Record<string, unknown>)[key] = value;
            }
        }
    }
    return merged;
}

export function resolveUnitCategoryName(
    unit: Pick<SingleUnit, 'categoryId' | 'category'> | null | undefined,
    groupedEquipment: tGroupedEquipment[] = [],
): string {
    if (!unit) {
        return '-';
    }

    if (unit.category?.name) {
        return unit.category.name;
    }

    if (unit.categoryId) {
        const fromGrouped = groupedEquipment.find((group) => group.id === unit.categoryId);
        if (fromGrouped?.name) {
            return fromGrouped.name;
        }
    }

    return '-';
}

export function resolveUnitCategoryFormId(
    unit: Pick<SingleUnit, 'categoryId' | 'category'> | null | undefined,
    groupedEquipment: tGroupedEquipment[] = [],
): string {
    if (!unit) {
        return '';
    }

    if (unit.category?.formId) {
        return unit.category.formId;
    }

    if (unit.categoryId) {
        const fromGrouped = groupedEquipment.find((group) => group.id === unit.categoryId);
        if (fromGrouped?.formId) {
            return fromGrouped.formId;
        }
    }

    return '';
}

type EquipmentInspectionFormCandidate = {
    name?: string | null;
    dailyServiceLogForm?: boolean | null;
    formType?: string | null;
};

/** Forms eligible for equipment category/unit inspection assignment (excludes daily logs and training quizzes). */
export function isEquipmentInspectionFormOption(form: EquipmentInspectionFormCandidate): boolean {
    if (form.dailyServiceLogForm) {
        return false;
    }

    if (form.formType === 'trainingQuiz') {
        return false;
    }

    if (form.name?.toLowerCase().includes('quiz')) {
        return false;
    }

    return true;
}

export function filterEquipmentInspectionForms<T extends EquipmentInspectionFormCandidate>(forms: T[]): T[] {
    return forms.filter(isEquipmentInspectionFormOption);
}

/** Case-insensitive A–Z by `name`, leaving the original array unchanged. */
export function sortRecordsByName<T extends { name?: string | null }>(items: T[]): T[] {
    return [...items].sort((a, b) =>
        (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }),
    );
}

export const formatAcquisitionCost = (value?: number | null): string => {
    if (value == null || Number.isNaN(value)) {
        return '-';
    }

    return value.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
};

/** Normalize stored/edited acquisition cost to a finite number or null. */
export const coerceAcquisitionCost = (value: unknown): number | null => {
    if (value == null || value === '') return null;
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    const cleaned = String(value).replace(/[$,\s]/g, '');
    if (!cleaned || cleaned === '.') return null;
    const parsed = Number.parseFloat(cleaned);
    return Number.isFinite(parsed) ? parsed : null;
};

const GEN2_WRITABLE_FIELD_SET = new Set<string>(GEN2_SINGLE_UNIT_WRITABLE_FIELDS);
const GEN2_FLOAT_FIELD_SET = new Set<string>(GEN2_SINGLE_UNIT_FLOAT_FIELDS);

/**
 * Keep only Gen2-writable SingleUnit keys and coerce float fields so GraphQL
 * does not reject currency/number strings from the edit form.
 */
export function sanitizeGen2SingleUnitWritePayload(
    updates: Record<string, unknown>,
    options?: { dropEmptyStrings?: boolean },
): Record<string, unknown> {
    const dropEmptyStrings = options?.dropEmptyStrings ?? false;
    const sanitized: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(updates)) {
        if (!GEN2_WRITABLE_FIELD_SET.has(key) || value === undefined) {
            continue;
        }
        if (dropEmptyStrings && value === '') {
            continue;
        }

        if (key === 'image' && (value === null || value === '')) {
            continue;
        }

        if ((key === 'assignedTo' || key === 'serviceCenterId') && value === '') {
            sanitized[key] = null;
            continue;
        }

        if (GEN2_FLOAT_FIELD_SET.has(key)) {
            if (key === 'replacedUMCounterValue') {
                if (value === null || value === '') {
                    sanitized.replacedUMCounterValue = 0;
                    sanitized.replacedUMCounterStatus = false;
                    continue;
                }
                const coerced = Number(value);
                if (Number.isNaN(coerced)) {
                    continue;
                }
                const clamped = Math.max(0, coerced);
                sanitized.replacedUMCounterValue = clamped;
                sanitized.replacedUMCounterStatus = clamped > 0;
                continue;
            }

            if (value === null || value === '') {
                if (!dropEmptyStrings) {
                    sanitized[key] = null;
                }
                continue;
            }
            const coerced =
                key === 'aquisitionCost' ? coerceAcquisitionCost(value) : Number(value);
            if (coerced == null || Number.isNaN(coerced)) {
                continue;
            }
            sanitized[key] = coerced;
            continue;
        }

        sanitized[key] = value;
    }

    return sanitized;
}

/** Group an integer digit string with commas (e.g. "1000000" → "1,000,000"). */
const groupIntegerDigits = (digits: string): string => {
    if (digits === '') return '';
    const normalized = digits.replace(/^0+(?=\d)/, '');
    return normalized.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

/**
 * Format a cleaned currency draft ("12", "12.", "12.5") with thousand separators.
 * Does not force trailing zeros so typing stays natural.
 */
export const formatCurrencyDraftDisplay = (cleaned: string): string => {
    if (cleaned === '') return '';
    const hasDecimal = cleaned.includes('.');
    const [intPart = '', fracPart = ''] = cleaned.split('.');
    const groupedInt = groupIntegerDigits(intPart === '' && hasDecimal ? '0' : intPart);
    if (!hasDecimal) return groupedInt;
    return `${groupedInt}.${fracPart}`;
};

/** Display/edit string for the currency input (no $ prefix; includes grouping + 2 decimals). */
export const formatAcquisitionCostForInput = (value: unknown): string => {
    const numeric = coerceAcquisitionCost(value);
    if (numeric == null) return '';
    return numeric.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
};

/** Keep only a valid partial currency amount while typing (e.g. "", "12", "12.", "12.5"). */
export const sanitizeCurrencyTyping = (raw: string): string | null => {
    const cleaned = raw.replace(/[$,\s]/g, '');
    if (cleaned === '') return '';
    if (!/^\d*\.?\d{0,2}$/.test(cleaned)) return null;
    return cleaned;
};

/** Map caret from raw typed value onto a comma-formatted display string. */
export const caretIndexAfterCurrencyFormat = (rawBeforeCaret: string, formatted: string): number => {
    const significantBefore = rawBeforeCaret.replace(/[$,\s]/g, '').length;
    let pos = 0;
    let counted = 0;
    while (pos < formatted.length && counted < significantBefore) {
        if (formatted[pos] !== ',') counted += 1;
        pos += 1;
    }
    return pos;
};

export function applyReplacedCounterEntries(
    formData: Partial<SingleUnit>,
    entries: ReplacedCounterEntry[],
): Partial<SingleUnit> {
    const total = entries.reduce((sum, entry) => sum + entry.value, 0);
    return {
        ...formData,
        replacedUMCounterHistory: entries.length > 0 ? JSON.stringify(entries) : null,
        replacedUMCounterValue: total,
        replacedUMCounterStatus: total > 0,
    } as Partial<SingleUnit>;
}

/**
 * Add a replaced-meter reading onto the cumulative total.
 * `reading` is the hours on the failed/replaced counter (not the new grand total).
 */
export function addReplacedCounterReading(
    formData: Partial<SingleUnit>,
    unit: UnitRecord,
    reading: number,
): Partial<SingleUnit> {
    if (!Number.isFinite(reading) || reading < 0) {
        return formData;
    }

    const currentTotal =
        formData.replacedUMCounterValue !== undefined
            ? Number(formData.replacedUMCounterValue) || 0
            : unit.replacedUMCounterValue ?? 0;

    const historyJson =
        (formData as Record<string, unknown>).replacedUMCounterHistory ?? unit.replacedUMCounterHistory;
    let entries = parseReplacedCounterHistory(typeof historyJson === 'string' ? historyJson : null);

    // Legacy total-only units: seed history so prior cumulative stays visible.
    if (entries.length === 0 && currentTotal > 0) {
        entries = [{ value: currentTotal, date: unit.putInServiceDate || '' }];
    }

    const nextEntries: ReplacedCounterEntry[] = [
        ...entries,
        {
            value: reading,
            date: new Date().toISOString().split('T')[0],
        },
    ];
    // Cumulative total is always the sum of recorded replacement readings.
    return applyReplacedCounterEntries(formData, nextEntries);
}

/** Entries for the replaced-counter editor (persisted history or legacy total-only value). */
export function resolveReplacedCounterEntries(
    unit: UnitRecord,
    formData: Partial<SingleUnit>,
): ReplacedCounterEntry[] {
    const historyJson =
        (formData as Record<string, unknown>).replacedUMCounterHistory ?? unit.replacedUMCounterHistory;
    const entries = parseReplacedCounterHistory(
        typeof historyJson === 'string' ? historyJson : null,
    );
    if (entries.length > 0) {
        return entries;
    }

    const total = formData.replacedUMCounterValue ?? unit.replacedUMCounterValue ?? 0;
    if (total > 0) {
        return [{ value: total, date: unit.putInServiceDate || '' }];
    }

    return [];
}

export const parseReplacedCounterHistory = (historyJson?: string | null): ReplacedCounterEntry[] => {
    if (!historyJson) return [];
    try {
        const parsed = JSON.parse(historyJson);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

/** Current reported counter plus replaced counter offset (PM, utilization, etc.). */
export function getEffectiveUMCounterReading(
    unit: Pick<SingleUnit, 'currentUMCounterValue' | 'replacedUMCounterValue'>,
): number | null {
    if (unit.currentUMCounterValue == null) {
        return null;
    }

    return unit.currentUMCounterValue + (unit.replacedUMCounterValue ?? 0);
}

type SchedulingUMUnit = Pick<SingleUnit, 'currentUMCounterValue' | 'replacedUMCounterValue'>;

/**
 * PM schedule total. Starts at zero and includes replaced meters.
 * Beginning UM is not subtracted.
 */
export function pmSchedulingUM(unit: SchedulingUMUnit | null | undefined): number {
    return getEffectiveUMCounterReading(unit ?? { currentUMCounterValue: null }) ?? 0;
}

/**
 * UM since Beginning UM, for utilization and dollars per UM.
 * Beginning UM stays an editable field. A missing beginning counts as zero.
 */
export function umSinceBeginning(
    unit: (SchedulingUMUnit & Pick<SingleUnit, 'machineUMCounterAtAquisition'>) | null | undefined,
): number {
    const effective = getEffectiveUMCounterReading(unit ?? { currentUMCounterValue: null });
    if (effective == null) return 0;
    const beginning = unit?.machineUMCounterAtAquisition;
    const start = beginning != null && Number.isFinite(beginning) ? beginning : 0;
    const usage = effective - start;
    return usage > 0 ? usage : 0;
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Parse stored unit/report dates without UTC day shift (YYYY-MM-DD or MM/DD/YYYY). */
export function parseUnitCalendarDate(value: string | null | undefined): Date | null {
    if (!value) {
        return null;
    }

    const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) {
        return new Date(Number(isoMatch[1]), Number(isoMatch[2]) - 1, Number(isoMatch[3]));
    }

    const slashMatch = value.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
    if (slashMatch) {
        return new Date(Number(slashMatch[3]), Number(slashMatch[1]) - 1, Number(slashMatch[2]));
    }

    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/** Archived units stay on file. `equipmentStatus === false` is the archived flag. */
export function isUnitArchived(unit: { equipmentStatus?: boolean | null } | null | undefined): boolean {
    return unit?.equipmentStatus === false;
}

/** Calendar day difference (end − start), not counting both endpoints. */
export function calendarDaysBetweenInclusiveStart(start: Date, end: Date): number {
    return Math.round((end.getTime() - start.getTime()) / MS_PER_DAY);
}

/** Inclusive calendar days from start through end (Jun 1 → Jul 23 = 53). */
export function calendarDaysInclusive(start: Date, end: Date): number {
    return calendarDaysBetweenInclusiveStart(start, end) + 1;
}

/** Older of last inspection vs last daily service (either may be missing). */
export function resolveOlderInspectionOrServiceDate(
    lastInspectionDate?: string | null,
    lastDailyServiceDate?: string | null,
): Date | null {
    const inspection = parseUnitCalendarDate(lastInspectionDate);
    const daily = parseUnitCalendarDate(lastDailyServiceDate);

    if (inspection && daily) {
        return inspection.getTime() <= daily.getTime() ? inspection : daily;
    }

    return inspection ?? daily;
}

/** Newer of last inspection vs last daily service (either may be missing). */
export function resolveNewerInspectionOrServiceDate(
    lastInspectionDate?: string | null,
    lastDailyServiceDate?: string | null,
): Date | null {
    const inspection = parseUnitCalendarDate(lastInspectionDate);
    const daily = parseUnitCalendarDate(lastDailyServiceDate);

    if (inspection && daily) {
        return inspection.getTime() >= daily.getTime() ? inspection : daily;
    }

    return inspection ?? daily;
}

/**
 * End of ITD day window (Marks date B): most recent of last inspection vs last daily service.
 * Must be on or after put-in-service.
 */
export function resolveItdActivityEndDate(
    lastInspectionDate: string | null | undefined,
    lastDailyServiceDate: string | null | undefined,
    serviceStart: Date,
): Date | null {
    const newer = resolveNewerInspectionOrServiceDate(lastInspectionDate, lastDailyServiceDate);
    if (!newer || newer.getTime() < serviceStart.getTime()) {
        return null;
    }

    return newer;
}

export function resolveItdServiceStartDate(
    putInServiceDate?: string | null,
    aquisitionDate?: string | null,
    inputDate?: string | null,
): Date | null {
    return (
        parseUnitCalendarDate(putInServiceDate) ??
        parseUnitCalendarDate(aquisitionDate) ??
        parseUnitCalendarDate(inputDate)
    );
}

export type ItdActivityUmSource = 'inspection' | 'daily' | 'current' | null;

/**
 * Marks G: UM on date B — from the activity that defines B, else current counter.
 */
export function resolveItdActivityUmReading(input: {
    lastInspectionDate?: string | null;
    lastDailyServiceDate?: string | null;
    lastInspectionUMCounter?: number | null;
    lastDailyServiceUMCounter?: number | null;
    currentUMCounterValue?: number | null;
}): { reading: number | null; source: ItdActivityUmSource } {
    const inspection = parseUnitCalendarDate(input.lastInspectionDate);
    const daily = parseUnitCalendarDate(input.lastDailyServiceDate);

    const pickInspection = (): { reading: number | null; source: ItdActivityUmSource } => {
        if (input.lastInspectionUMCounter != null && Number.isFinite(input.lastInspectionUMCounter)) {
            return { reading: input.lastInspectionUMCounter, source: 'inspection' };
        }
        if (input.currentUMCounterValue != null && Number.isFinite(input.currentUMCounterValue)) {
            return { reading: input.currentUMCounterValue, source: 'current' };
        }
        return { reading: null, source: null };
    };

    const pickDaily = (): { reading: number | null; source: ItdActivityUmSource } => {
        if (input.lastDailyServiceUMCounter != null && Number.isFinite(input.lastDailyServiceUMCounter)) {
            return { reading: input.lastDailyServiceUMCounter, source: 'daily' };
        }
        if (input.currentUMCounterValue != null && Number.isFinite(input.currentUMCounterValue)) {
            return { reading: input.currentUMCounterValue, source: 'current' };
        }
        return { reading: null, source: null };
    };

    if (inspection && daily) {
        return inspection.getTime() >= daily.getTime() ? pickInspection() : pickDaily();
    }
    if (inspection) {
        return pickInspection();
    }
    if (daily) {
        return pickDaily();
    }
    if (input.currentUMCounterValue != null && Number.isFinite(input.currentUMCounterValue)) {
        return { reading: input.currentUMCounterValue, source: 'current' };
    }
    return { reading: null, source: null };
}

export type ItdUtilizationMetrics = {
    grossDays: number;
    netDays: number;
    periodUm: number | null;
    annualPace: number | null;
    percent: number | null;
};

export type ItdUtilizationInput = {
    putInServiceDate?: string | null;
    aquisitionDate?: string | null;
    inputDate?: string | null;
    lastInspectionDate?: string | null;
    lastDailyServiceDate?: string | null;
    lastInspectionUMCounter?: number | null;
    lastDailyServiceUMCounter?: number | null;
    /** F — beginning UM at place-in-service */
    beginningUmAtService?: number | null;
    /**
     * G — UM on last activity date. When omitted, resolved from inspection/daily/current.
     * Legacy callers may pass `effectiveUmReading` as G+H already; prefer G + replaced separately.
     */
    activityUmReading?: number | null;
    /** @deprecated Prefer activityUmReading (G) + replacedUmCounterValue (H). Treated as G+H when set and activityUmReading omitted. */
    effectiveUmReading?: number | null;
    /** H — replaced counter total */
    replacedUmCounterValue?: number | null;
    currentUMCounterValue?: number | null;
    /** U — anticipated annual UM */
    annualProjectedUsage?: number | null;
    /** D — adjustment days (user input) */
    adjustmentDays?: number | null;
};

/**
 * Marks ITD Utilization metrics:
 * C = inclusive days A→B; E = C − D; J = (G + H) − F;
 * AP = (J/E)×365; ITD% = (AP/U)×100
 *
 * Returns day window whenever A/B are valid. `percent` is null when U/J/E are incomplete.
 */
export function calculateItdUtilizationMetrics(input: ItdUtilizationInput): ItdUtilizationMetrics | null {
    const serviceStart = resolveItdServiceStartDate(
        input.putInServiceDate,
        input.aquisitionDate,
        input.inputDate,
    );
    const activityEnd = serviceStart
        ? resolveItdActivityEndDate(input.lastInspectionDate, input.lastDailyServiceDate, serviceStart)
        : null;
    if (!serviceStart || !activityEnd) {
        return null;
    }

    const grossDays = calendarDaysInclusive(serviceStart, activityEnd);
    if (grossDays <= 0) {
        return null;
    }

    const rawAdjustment =
        typeof input.adjustmentDays === 'number' ? input.adjustmentDays : Number(input.adjustmentDays ?? 0);
    const adjustmentDays = Number.isFinite(rawAdjustment) ? Math.max(0, Math.min(grossDays, rawAdjustment)) : 0;
    const netDays = grossDays - adjustmentDays;

    const projected =
        typeof input.annualProjectedUsage === 'number'
            ? input.annualProjectedUsage
            : Number(input.annualProjectedUsage);

    const beginningUm = input.beginningUmAtService ?? 0;
    const replacedUm =
        input.replacedUmCounterValue != null && Number.isFinite(input.replacedUmCounterValue)
            ? input.replacedUmCounterValue
            : 0;

    let periodUm: number | null = null;
    if (input.activityUmReading != null && Number.isFinite(input.activityUmReading)) {
        periodUm = input.activityUmReading + replacedUm - beginningUm;
    } else if (input.effectiveUmReading != null && Number.isFinite(input.effectiveUmReading)) {
        periodUm = input.effectiveUmReading - beginningUm;
    } else {
        const resolved = resolveItdActivityUmReading({
            lastInspectionDate: input.lastInspectionDate,
            lastDailyServiceDate: input.lastDailyServiceDate,
            lastInspectionUMCounter: input.lastInspectionUMCounter,
            lastDailyServiceUMCounter: input.lastDailyServiceUMCounter,
            currentUMCounterValue: input.currentUMCounterValue,
        });
        if (resolved.reading != null) {
            periodUm = resolved.reading + replacedUm - beginningUm;
        }
    }

    if (
        !Number.isFinite(projected) ||
        projected <= 0 ||
        netDays <= 0 ||
        periodUm == null ||
        periodUm <= 0
    ) {
        return {
            grossDays,
            netDays,
            periodUm: periodUm != null && periodUm > 0 ? periodUm : null,
            annualPace: null,
            percent: null,
        };
    }

    const annualPace = (periodUm / netDays) * 365;
    if (annualPace <= 0) {
        return {
            grossDays,
            netDays,
            periodUm,
            annualPace: null,
            percent: null,
        };
    }

    return {
        grossDays,
        netDays,
        periodUm,
        annualPace,
        percent: (annualPace / projected) * 100,
    };
}

/** ITD Utilization % (Marks pace formula). Returns null when inputs are incomplete. */
export function calculateItdUtilizationPacePercent(input: ItdUtilizationInput): number | null {
    return calculateItdUtilizationMetrics(input)?.percent ?? null;
}

export function formatReplacedUMCounterDisplay(
    unit: Pick<SingleUnit, 'replacedUMCounterValue'> & Pick<UnitRecord, 'replacedUMCounterHistory'>,
): string {
    const history = parseReplacedCounterHistory(unit.replacedUMCounterHistory);
    const total = unit.replacedUMCounterValue ?? 0;

    if (history.length > 0) {
        const historyTotal = history.reduce((sum, entry) => sum + entry.value, 0);
        return `${historyTotal.toLocaleString()} (${history.length} replacement${history.length > 1 ? 's' : ''})`;
    }

    return total.toLocaleString();
}
